import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AddressDetails {
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  formatted_address: string;
  latitude: number | null;
  longitude: number | null;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

/** Edge function `google-places` returns these when something fails. */
function messageForPlacesResponse(data: {
  error?: string;
  errorCode?: string;
} | null): string {
  const code = data?.errorCode;
  if (code === "MISSING_GOOGLE_MAPS_API_KEY") {
    return "Address search is not configured: add the secret GOOGLE_MAPS_API_KEY under Supabase → Edge Functions → Secrets (exact name), not only project env vars elsewhere.";
  }
  if (code === "GOOGLE_PLACES_REJECTED") {
    return "Google Places blocked this request: enable billing, enable Places API on the key’s Google Cloud project, and avoid “HTTP referrer”–only restrictions (Edge Functions call Google from the server).";
  }
  if (code === "GOOGLE_PLACES_NETWORK") {
    return "Could not reach Google Places. Check connection and try again.";
  }
  if (code === "INTERNAL" || code === "INVALID_REQUEST") {
    return data?.error ?? "Address lookup failed. Try again.";
  }
  if (data?.error?.toLowerCase().includes("not configured")) {
    return "Address search is not configured (missing GOOGLE_MAPS_API_KEY for Edge Functions).";
  }
  return "Address lookup failed. If keys are set in Supabase, confirm they are Edge Function secrets named GOOGLE_MAPS_API_KEY and that Places API + billing are enabled in Google Cloud.";
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (details: AddressDetails) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address...",
  disabled = false,
}: AddressAutocompleteProps) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const requestCounterRef = useRef(0);

  // Fetch autocomplete predictions
  const fetchPredictions = async (input: string) => {
    if (input.length < 3) {
      setPredictions([]);
      setIsOpen(false);
      setIsLoading(false);
      setApiError(null);
      return;
    }

    // Increment request counter to track the latest request
    requestCounterRef.current += 1;
    const currentRequest = requestCounterRef.current;

    setIsLoading(true);
    
    // Set a timeout to ensure loading state doesn't get stuck
    const timeoutId = setTimeout(() => {
      if (currentRequest === requestCounterRef.current) {
        console.warn("Request timeout for address autocomplete");
        setIsLoading(false);
        setPredictions([]);
        setIsOpen(false);
      }
    }, 10000); // 10 second timeout

    try {
      const result = await supabase.functions.invoke("google-places", {
        body: { action: "autocomplete", input },
      });

      clearTimeout(timeoutId);

      // Only process if this is still the latest request
      if (currentRequest !== requestCounterRef.current) {
        return;
      }

      const { data, error } = result;

      if (error) {
        console.error("Supabase function error:", error);
        const raw = (error as { message?: string }).message ?? String(error);
        const low = raw.toLowerCase();
        if (low.includes("jwt") || low.includes("401") || low.includes("unauthorized")) {
          setApiError("Your session may have expired. Refresh the page, sign in again, then retry address search.");
        } else if (low.includes("404") || low.includes("not found")) {
          setApiError("The google-places Edge Function was not found. Deploy Edge Functions for this Supabase project.");
        } else {
          setApiError(`Address lookup unavailable: ${raw}`);
        }
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      // Handle case where data might be null or undefined
      if (!data) {
        setApiError("Address lookup unavailable. Please try again.");
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      // Check if the response contains an error in the data
      if (data.error) {
        console.error("Google Places API error:", data.error, (data as { errorCode?: string }).errorCode);
        setApiError(messageForPlacesResponse(data as { error?: string; errorCode?: string }));
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      // Extract predictions - handle different possible response structures
      let predictions: Prediction[] = [];
      
      if (Array.isArray(data.predictions)) {
        predictions = data.predictions;
      } else if (Array.isArray(data)) {
        predictions = data;
      } else if (data.data && Array.isArray(data.data.predictions)) {
        predictions = data.data.predictions;
      }
      
      // Validate prediction structure
      const validPredictions = predictions.filter((p: any) => 
        p && (p.place_id || p.description)
      );

      setApiError(null);
      setPredictions(validPredictions);
      setIsOpen(validPredictions.length > 0);
      setIsLoading(false);
    } catch (err) {
      clearTimeout(timeoutId);
      
      // Only process if this is still the latest request
      if (currentRequest !== requestCounterRef.current) {
        return;
      }
      console.error("Error fetching predictions:", err);
      setApiError("Address lookup unavailable. Please try again.");
      setPredictions([]);
      setIsOpen(false);
      setIsLoading(false);
    }
  };

  // Fetch place details when a prediction is selected
  const fetchPlaceDetails = async (placeId: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-places", {
        body: { action: "details", placeId },
      });

      if (error) {
        console.error("Supabase function error:", error);
        return;
      }

      // Check if the response contains an error in the data
      if (data?.error) {
        console.error("Google Places Details API error:", data.error);
        return;
      }

      if (data?.place) {
        onSelect(data.place);
        onChange(data.place.street_address || data.place.formatted_address);
      }
    } catch (err) {
      console.error("Error fetching place details:", err);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
      setPredictions([]);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setHighlightedIndex(-1);

    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // If input is too short, clear predictions immediately
    if (newValue.length < 3) {
      setPredictions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          fetchPlaceDetails(predictions[highlightedIndex].place_id);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Calculate dropdown position
  useEffect(() => {
    if (!isOpen || predictions.length === 0 || !inputRef.current || !dropdownRef.current) {
      return;
    }

    const updatePosition = () => {
      const input = inputRef.current;
      const dropdown = dropdownRef.current;
      
      if (input && dropdown) {
        const rect = input.getBoundingClientRect();
          const viewportPadding = 8;
          const maxLeft = window.innerWidth - rect.width - viewportPadding;
          const left = Math.max(viewportPadding, Math.min(rect.left, maxLeft));
        
        dropdown.style.position = "fixed";
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${left}px`;
        dropdown.style.width = `${rect.width}px`;
        dropdown.style.zIndex = "9999";
        dropdown.style.pointerEvents = "auto";
        dropdown.style.visibility = "visible";
        dropdown.style.opacity = "1";
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(updatePosition, 0);
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, predictions.length, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    // Use a small delay to avoid closing immediately when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => predictions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pr-8"
        />
        {isLoading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {apiError && (
        <p className="mt-1 text-xs text-destructive">{apiError}</p>
      )}

      {isOpen && predictions.length > 0 && typeof document !== "undefined" && createPortal(
        <div 
          ref={dropdownRef}
          className="z-[9999] bg-popover border border-border rounded-md shadow-lg"
          style={{ 
            position: "fixed",
            pointerEvents: "auto",
            zIndex: 9999,
            maxHeight: "240px",
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain"
          }}
          onWheel={(e) => {
            // Allow wheel scrolling
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Allow touch scrolling
            e.stopPropagation();
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={cn(
                "w-full px-3 py-2 text-left text-sm flex items-start gap-2 hover:bg-accent cursor-pointer",
                highlightedIndex === index && "bg-accent"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fetchPlaceDetails(prediction.place_id);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={(e) => {
                // Don't prevent default on mousedown to allow scrolling
                e.stopPropagation();
              }}
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                {prediction.structured_formatting ? (
                  <>
                    <div className="font-medium truncate">
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </>
                ) : (
                  <div className="truncate">{prediction.description}</div>
                )}
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
